import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Define color palette
const primaryColor = '#4E67EB'; // Main blue for brand
const secondaryColor = '#FF4E71'; // Coral accent
const infoColor = '#00B3E6'; // Light blue for information
const successColor = '#059669'; // Darker emerald green for success states
const warningColor = '#FFB648'; // Orange for warnings
const errorColor = '#FF4B4B'; // Red for errors

// Create theme based on light/dark mode
export const createAppTheme = (_mode?: 'light' | 'dark') => {
  // Single-mode theme (dark), app background color per spec
  const backgroundDefault = '#1F2129';
  const backgroundPaper = '#1F2129';
  const textPrimary = '#FFFFFF';
  const textSecondary = 'rgba(255, 255, 255, 0.72)';
  const dividerColor = 'rgba(255, 255, 255, 0.12)';

  const baseTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: primaryColor,
        light: '#6272D6',
        dark: '#3B4CB1',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondaryColor,
        light: '#E55A76',
        dark: '#BD364F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: infoColor,
      },
      success: {
        main: successColor,
      },
      warning: {
        main: warningColor,
      },
      error: {
        main: errorColor,
      },
      background: {
        default: backgroundDefault,
        paper: backgroundPaper,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: dividerColor,
    },
    typography: {
      fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 800,
        fontSize: '2.75rem',
        letterSpacing: '-0.03em',
      },
      h2: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 700,
        fontSize: '2.25rem',
        letterSpacing: '-0.025em',
      },
      h3: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 600,
        fontSize: '1.875rem',
        letterSpacing: '-0.02em',
      },
      h4: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 600,
        fontSize: '1.5rem',
        letterSpacing: '-0.015em',
      },
      h5: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 600,
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
      },
      h6: {
        fontFamily: '"Figtree", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 600,
        fontSize: '1rem',
        letterSpacing: '-0.005em',
      },
      body1: {
        fontSize: '1rem',
      },
      body2: {
        fontSize: '0.875rem',
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.05)',
      '0px 4px 8px rgba(0, 0, 0, 0.05)',
      '0px 8px 16px rgba(0, 0, 0, 0.05)',
      '0px 16px 24px rgba(0, 0, 0, 0.05)',
      '0px 20px 40px rgba(0, 0, 0, 0.05)',
      '0px 24px 48px rgba(0, 0, 0, 0.05)',
      '0px 28px 56px rgba(0, 0, 0, 0.05)',
      '0px 32px 64px rgba(0, 0, 0, 0.05)',
      '0px 36px 72px rgba(0, 0, 0, 0.05)',
      '0px 40px 80px rgba(0, 0, 0, 0.05)',
      '0px 44px 88px rgba(0, 0, 0, 0.05)',
      '0px 48px 96px rgba(0, 0, 0, 0.05)',
      '0px 52px 104px rgba(0, 0, 0, 0.05)',
      '0px 56px 112px rgba(0, 0, 0, 0.05)',
      '0px 60px 120px rgba(0, 0, 0, 0.05)',
      '0px 64px 128px rgba(0, 0, 0, 0.05)',
      '0px 68px 136px rgba(0, 0, 0, 0.05)',
      '0px 72px 144px rgba(0, 0, 0, 0.05)',
      '0px 76px 152px rgba(0, 0, 0, 0.05)',
      '0px 80px 160px rgba(0, 0, 0, 0.05)',
      '0px 84px 168px rgba(0, 0, 0, 0.05)',
      '0px 88px 176px rgba(0, 0, 0, 0.05)',
      '0px 92px 184px rgba(0, 0, 0, 0.05)',
      '0px 96px 192px rgba(0, 0, 0, 0.05)',
    ],
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            top: 0,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 20px',
            boxShadow: 'none',
            textTransform: 'uppercase',
            minHeight: '40px',
            letterSpacing: '0.1em',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          contained: {
            borderRadius: '24px',
          },
          outlined: {
            borderRadius: '24px',
            borderColor: '#5D54FF',
            borderWidth: '1.25px',
          },
          text: {
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
            '&:active': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
            },
          },
          containedPrimary: {
            background: `linear-gradient(45deg, ${primaryColor} 30%, #465AD9 90%)`,
          },
          containedSecondary: {
            background: `linear-gradient(45deg, ${secondaryColor} 30%, #E64A6A 90%)`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
          elevation2: {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          },
          elevation3: {
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              minHeight: '40px',
              '& fieldset': {
                borderColor: '#474950',
              },
              '&:hover': {
                backgroundColor: '#1A1C32',
                '& fieldset': {
                  borderColor: '#4F5FEB',
                },
              },
              '&.Mui-focused': {
                backgroundColor: '#16182B',
                '& fieldset': {
                  borderColor: '#4F5FEB',
                },
              },
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: backgroundDefault,
            color: textPrimary,
            transition: 'background-color 0.2s ease',
            height: '100%',
            overflow: 'auto',
            margin: 0,
            padding: 0,
          },
          html: {
            height: '100%',
            overflow: 'auto',
          },
          '#root': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          // Fix scrolling issues
          '.MuiBox-root': {
            '&[style*="minHeight: 100vh"]': {
              minHeight: '100vh !important',
              height: 'auto !important',
            },
          },
          // Ensure proper overflow handling
          '.MuiDrawer-root, .MuiDrawer-paper': {
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
          },
          // Fix any potential double scrolling
          'body > *': {
            overflow: 'visible !important',
          },
          // Ensure proper viewport handling
          'html, body': {
            scrollBehavior: 'smooth',
          },
          ':root': {
            '--app-footer-height': '52px',
            '--nav-role-switcher-height': '108px',
          },
          // Compact dashboard sidebar (Toolpad DashboardLayout)
          '.MuiDrawer-root': {
            height: '100vh',
            alignSelf: 'stretch',
          },
          '.MuiDrawer-paper': {
            backgroundColor: '#181A22',
            borderRight: '1px solid rgba(255, 255, 255, 0.06)',
            overflowX: 'hidden !important',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh !important',
            maxHeight: '100vh !important',
            position: 'relative',
            containerType: 'inline-size',
            containerName: 'dashboard-sidebar',
            // Subtle, barely-visible scrollbar
            scrollbarWidth: 'thin',
            scrollbarColor: 'transparent transparent',
            '&:hover': {
              scrollbarColor: 'rgba(255, 255, 255, 0.12) transparent',
            },
          },
          '.MuiDrawer-paper::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '.MuiDrawer-paper::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '.MuiDrawer-paper::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
          },
          '.MuiDrawer-paper:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
          },
          '.MuiDrawer-paper::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          '.MuiDrawer-paper > .MuiToolbar-root': {
            flexShrink: 0,
          },
          '.MuiDrawer-paper nav': {
            position: 'absolute !important',
            top: '56px',
            right: 0,
            bottom: 'var(--app-footer-height)',
            left: 0,
            paddingTop: '8px !important',
            overflowX: 'hidden',
            overflowY: 'hidden !important',
            height: 'auto !important',
            flex: 'unset !important',
            display: 'flex !important',
            flexDirection: 'column !important',
            justifyContent: 'flex-start !important',
          },
          '@media (min-width: 600px)': {
            '.MuiDrawer-paper nav': {
              top: '64px',
            },
          },
          // Scrollable menu area — keeps role switcher pinned at sidebar bottom
          '.MuiDrawer-paper nav > *:first-of-type': {
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto !important',
            overflowX: 'hidden',
            paddingBottom: 'var(--nav-role-switcher-height)',
          },
          '.MuiDrawer-paper .navigation-role-switcher': {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
          },
          '.MuiDrawer-paper nav > .MuiList-root': {
            marginBottom: '8px !important',
          },
          '.MuiDrawer-paper nav .MuiListSubheader-root': {
            height: '26px',
            lineHeight: '26px',
            padding: '2px 12px 0',
            marginTop: '6px',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            color: 'rgba(255, 255, 255, 0.45)',
            overflow: 'hidden',
          },
          '.MuiDrawer-paper nav .MuiListItem-root': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: '6px',
            paddingRight: '6px',
            overflow: 'hidden',
          },
          '.MuiDrawer-paper nav .MuiListItemButton-root': {
            height: '34px !important',
            minHeight: '34px',
            padding: '4px 10px',
            borderRadius: '6px',
            margin: '1px 0',
            overflow: 'hidden',
          },
          '.MuiDrawer-paper nav .MuiListItemButton-root.Mui-selected': {
            border: 'none',
            backgroundColor: 'rgba(78, 103, 235, 0.14)',
            boxShadow: 'inset 2px 0 0 #4E67EB',
          },
          '.MuiDrawer-paper nav .MuiListItemButton-root.Mui-selected:hover': {
            backgroundColor: 'rgba(78, 103, 235, 0.18)',
          },
          '.MuiDrawer-paper nav .MuiListItemIcon-root': {
            minWidth: '28px !important',
            marginRight: '8px !important',
          },
          '.MuiDrawer-paper nav .MuiListItemIcon-root .MuiSvgIcon-root': {
            fontSize: '1.05rem',
          },
          '.MuiDrawer-paper nav .MuiListItemText-root': {
            overflow: 'hidden',
          },
          '.MuiDrawer-paper nav .MuiListItemText-root .MuiTypography-root': {
            fontSize: '0.8125rem',
            fontWeight: 500,
            letterSpacing: '-0.01em',
          },
          // Collapsed (mini) drawer — icons only, no label bleed
          '@container dashboard-sidebar (max-width: 70px)': {
            '.MuiDrawer-paper nav .MuiListSubheader-root': {
              height: '0 !important',
              minHeight: 0,
              padding: '0 !important',
              margin: '0 !important',
              opacity: 0,
              visibility: 'hidden',
            },
            '.MuiDrawer-paper nav > .MuiList-root': {
              marginBottom: '2px !important',
            },
            '.MuiDrawer-paper nav .MuiListItem-root': {
              paddingLeft: '4px',
              paddingRight: '4px',
              justifyContent: 'center',
            },
            '.MuiDrawer-paper nav .MuiListItemButton-root': {
              justifyContent: 'center',
              padding: '6px 0 !important',
              width: '100%',
            },
            '.MuiDrawer-paper nav .MuiListItemIcon-root': {
              minWidth: '0 !important',
              marginRight: '0 !important',
              justifyContent: 'center',
            },
            '.MuiDrawer-paper nav .MuiListItemText-root': {
              display: 'none !important',
              width: 0,
              minWidth: 0,
              flex: '0 0 0',
              overflow: 'hidden',
            },
          },

        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 48,
            height: 26,
            padding: 0,
          },
          switchBase: {
            padding: 1,
            '&.Mui-checked': {
              transform: 'translateX(22px)',
              color: '#4F5FEB !important',
              '& + .MuiSwitch-track': {
                backgroundColor: '#4F5FEB !important',
                border: '1.25px solid #4F5FEB !important',
              },
            },
            '&.Mui-checked:hover + .MuiSwitch-track': {
              backgroundColor: '#4F5FEB !important',
              border: '1.25px solid #4F5FEB !important',
            },
          },
          thumb: {
            width: 24,
            height: 24,
            backgroundColor: '#f5f5f5 !important',
            border: '1px solid #4F5FEB !important',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2) !important',
          },
          track: {
            borderRadius: 13,
            backgroundColor: '#474950 !important',
            border: '1.25px solid rgb(93, 84, 255) !important',
            opacity: '1 !important',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#4F5FEB',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#4F5FEB',
            },
          },
        },
      },
      // Navigation styling for DashboardLayout
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            margin: '1px 0',
            minHeight: '34px',
            padding: '4px 10px',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            '&.Mui-selected': {
              border: 'none',
              backgroundColor: 'rgba(78, 103, 235, 0.14)',
              boxShadow: 'inset 2px 0 0 #4E67EB',
              '&:hover': {
                backgroundColor: 'rgba(78, 103, 235, 0.18)',
                color: 'inherit',
              },
            },
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              color: 'inherit',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '28px',
            marginRight: '8px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.05rem',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            margin: 0,
            '& .MuiTypography-root': {
              fontSize: '0.8125rem',
              fontWeight: 500,
              letterSpacing: '-0.01em',
            },
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.45)',
            padding: '2px 12px 0',
            marginTop: '6px',
            lineHeight: 1.2,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: '#1A1C32',
            },
            '&.Mui-focused': {
              backgroundColor: '#16182B',
            },
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });

  return responsiveFontSizes(baseTheme);
};

// Default theme (light mode)
const theme = createAppTheme('light');

export default theme; 